// use-history-location.ts
//
// A tiny navigation layer over the browser History API. Each portal keeps its
// whole in-app "location" as one serializable object; this hook mirrors it into
// `window.history` so the browser Back/Forward buttons move through the in-app
// navigation instead of leaving the portal.
//
// - `navigate(next)`   → pushState (a new history entry) + update state
// - `navigate(next, { replace: true })` → replaceState (no new entry)
// - `back()`           → window.history.back() (one level up)
// - Back/Forward (popstate) → restores the location stored on that entry
//
// The location is stamped under a namespaced key so we don't clobber any other
// history state (e.g. the `?mod` portal selector).
import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = '__portalLoc';

function readLoc<T>(state: unknown, fallback: T): T {
  if (state && typeof state === 'object' && KEY in (state as Record<string, unknown>)) {
    return (state as Record<string, unknown>)[KEY] as T;
  }
  return fallback;
}

interface HistoryLocation<T> {
  location: T;
  navigate: (next: T, opts?: { replace?: boolean }) => void;
  back: () => void;
}

export function useHistoryLocation<T>(initial: T): HistoryLocation<T> {
  const [location, setLocation] = useState<T>(() => readLoc(window.history.state, initial));
  const initialRef = useRef(initial);

  useEffect(() => {
    // Stamp the current entry with our initial location so a Back that lands
    // here (or a reload) restores it instead of an empty state.
    const current = window.history.state;
    if (!(current && typeof current === 'object' && KEY in current)) {
      window.history.replaceState({ ...(current as object ?? {}), [KEY]: location }, '');
    }
    const onPop = (e: PopStateEvent) => setLocation(readLoc(e.state, initialRef.current));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // Run once: this owns the history sync for the portal's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useCallback((next: T, opts?: { replace?: boolean }) => {
    setLocation(next);
    const current = window.history.state;
    const merged = { ...((current && typeof current === 'object' ? current : {}) as object), [KEY]: next };
    if (opts?.replace) window.history.replaceState(merged, '');
    else window.history.pushState(merged, '');
  }, []);

  const back = useCallback(() => window.history.back(), []);

  return { location, navigate, back };
}
